// src/components/SummaryReports/utils/summaryReportColumns.ts

/**
 * Building the Summary Reports grid columns from the service schema and our own field
 * configuration.
 *
 * This grid pivots, aggregates and charts its rows, and all three go quietly wrong on a
 * number the service sent as text — so numbers are coerced here. Some fields also need
 * keeping out of the charts panel entirely.
 */

import type {
    GridColDef,
    GridRenderCellParams,
    GridTreeNodeWithRender,
    GridValueFormatter,
} from '@mui/x-data-grid';
import type { ReactNode } from 'react';

import { formatDateForDisplay } from '../../../utils/DateHelper.tsx';
import { mapEsriTypeToMuiType } from '../../../utils/GridColumnHelper.ts';
import { coerceNumericValue } from '../../../utils/NumericValue.ts';

/** How one column is described in the configuration. */
export interface SummaryReportFieldConfiguration {
    field: string;
    editable?: boolean;
    valueFormatter?: GridValueFormatter;
    renderCell?: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => ReactNode;
    alias?: string;
    /**
     * Whether the charts panel may offer this field.
     *
     * Unset, the grid decides — which means yes for everything but booleans, since the date
     * and number column types inherit `chartable: true` from the string type. Set it false
     * for fields no chart can use.
     */
    chartable?: boolean;
    /**
     * Treat this as a number even though the schema calls it a string.
     *
     * Columns the schema already types as numeric need no flag. This is for the other case,
     * where the schema itself is wrong.
     *
     * Not for codes that merely look numeric — district numbers, cost codes, zips. Coercion
     * drops a leading zero and turns a category into a measure.
     */
    numeric?: boolean;
    /**
     * Which aggregations this field may take, narrowing what the configuration offers.
     *
     * Unset, every function whose column types allow it is available, and a field charted
     * without an aggregation of its own is given the first of them. Name a single function
     * for a number that sums to nonsense: durations, rates, percentages, scores.
     */
    availableAggregations?: string[];
}

/** One warning per field per session — the getter runs for every cell of every render. */
const warnedNumericFields = new Set<string>();

const warnOnStringNumeric = (field: string, value: string) => {
    if (!import.meta.env?.DEV || warnedNumericFields.has(field)) return;
    warnedNumericFields.add(field);
    console.warn(
        `[summaryReportColumns] Numeric column "${field}" received the string ${JSON.stringify(
            value
        )}. Coercing it, but the service field is worth fixing at the source.`
    );
};

/** Reset between tests; not used by the app. */
export const resetStringNumericWarnings = () => warnedNumericFields.clear();

/** Builds the column definitions the grid is handed. */
export const buildSummaryReportColumns = (
    fields: __esri.Field[],
    gridFields: SummaryReportFieldConfiguration[] = []
): GridColDef[] => {
    return gridFields.map((field) => {
        const esriField = fields.find((f) => f.name === field.field);

        // `numeric` overrides the schema; everything else follows it.
        const muiType = field.numeric ? 'number' : mapEsriTypeToMuiType(esriField?.type || '');

        const colDef: GridColDef = {
            field: field.field,
            headerName: field.alias
                ? field.alias
                : esriField?.alias
                  ? esriField.alias
                  : field.field,
            editable: field.editable || false,
            width: 150,
            type: muiType || 'string',
        };

        // Every number column reads through the coercion, so a string from the service is a
        // number by the time sorting, filtering, aggregation, charts and the formatter see
        // it. The getter reads the field it writes, so edits still apply normally.
        if (muiType === 'number') {
            colDef.valueGetter = (value: unknown) => {
                if (typeof value === 'string' && value.trim() !== '') {
                    warnOnStringNumeric(field.field, value);
                }
                return coerceNumericValue(value);
            };
        }

        // The same date display as every other grid; a configured formatter wins.
        if (muiType === 'dateTime') {
            colDef.valueFormatter = (value) => {
                if (!value) return '';
                return formatDateForDisplay(value);
            };
        }

        if (field.valueFormatter) {
            colDef.valueFormatter = field.valueFormatter;
        }

        if (field.renderCell) {
            colDef.renderCell = field.renderCell;
        }

        // Only when the configuration takes a position. The grid merges a colDef over its
        // column type by spread, so `chartable: undefined` would clobber the type's own
        // default rather than defer to it.
        if (field.chartable !== undefined) {
            colDef.chartable = field.chartable;
        }

        // Same reasoning: MUI reads a present key as an opinion and consults it ahead of
        // each aggregation function's own column types.
        if (field.availableAggregations !== undefined) {
            colDef.availableAggregationFunctions = field.availableAggregations;
        }

        return colDef;
    });
};
